import { 
    uploadEvalFile, 
    createDataset 
} from '@/services/api';

import { 
    uploadFile, 
    validateArchiveFile 
} from '@/services/fileService';

const readJsonFile = async (file) => {
    try {
        const content = await file.text();
        return JSON.parse(content);
    } catch (error) {
        console.warn(error);
        throw new Error('Invalid JSON format or corrupted file');
    }
};

const renameFile = (file, newName) => {
    try {
        return new File([file], newName, { 
            type: file.type || 'application/octet-stream', 
            lastModified: file.lastModified 
        });
    } catch (e) {
        console.warn(`Error renaming file to ${newName}, using original:`, e);
        return file;
    }
};

const getArchiveExtension = (fileName) => {
    if (!fileName) return null;
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.tar.gz')) return 'tar.gz';
    if (lower.endsWith('.zip')) return 'zip';
    return null;
};

export const isValidEvalExtension = (fileName) => {
    if (!fileName) return false;
    const lower = fileName.toLowerCase();
    return lower.endsWith('.json') || lower.endsWith('.txt');
};

export const isValidArchiveExtension = (fileName) => {
    if (!fileName) return false;
    return !!getArchiveExtension(fileName);
};

export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validates the criteria list of an answer.
 * Rules:
 * 1. If it exists, it cannot be empty [].
 * 2. Weights must sum to 100%.
 * 3. Individual weight must be > 0 and <= 100.
 */
const validateCriteria = (criteria, taskId) => {
    if (!criteria) {
        return;
    }

    if (!Array.isArray(criteria)) {
        throw new Error(`Task ${taskId}: 'criteria' must be a valid array.`);
    }

    if (criteria.length === 0) {
        throw new Error(`Task ${taskId}: 'criteria' list cannot be empty found. Remove the property or add items.`);
    }

    let totalWeight = 0;

    criteria.forEach((criterion, index) => {
        if (!criterion.name || typeof criterion.name !== 'string' || criterion.name.trim() === '') {
            throw new Error(`Task ${taskId}: Criterion at position ${index + 1} has an empty name.`);
        }

        const weight = parseFloat(criterion.weight);

        if (isNaN(weight)) {
            throw new Error(`Task ${taskId}: Criterion '${criterion.name}' has an invalid weight format.`);
        }

        if (weight <= 0) {
            throw new Error(`Task ${taskId}: Criterion '${criterion.name}' has weight ${weight}%. Weight must be strictly greater than 0%.`);
        }

        if (weight > 100) {
            throw new Error(`Task ${taskId}: Criterion '${criterion.name}' has weight ${weight}%. Weight must be 100% or less.`);
        }

        totalWeight += weight;
    });

    if (Math.abs(totalWeight - 100) > 0.01) {
        throw new Error(`Task ${taskId}: Criteria weights sum to ${totalWeight.toFixed(2)}% but must equal exactly 100%.`);
    }
};

export const processTaskFile = async (file) => {
    if (!isValidEvalExtension(file.name)) {
        throw new Error('Invalid file. Use .json or .txt for Task.');
    }

    let content = null;
    let referenceFileRequired = false;
    const referenceFileNames = new Set();

    if (file.name.endsWith('.json')) {
        let rawContent = await readJsonFile(file);
        
        if (!Array.isArray(rawContent)) throw new Error('Task file must be a JSON Array');

        content = {
            version: 1,
            prompts: rawContent
        };

        const seenTaskIds = new Set();

        rawContent.forEach((task, index) => {
            if (!task.task_id) throw new Error(`Missing 'task_id' in item ${index + 1}`);
            
            if (seenTaskIds.has(task.task_id)) {
                throw new Error(`Duplicate task_id found: '${task.task_id}' (Row ${index + 1})`);
            }
            seenTaskIds.add(task.task_id);

            if (!task.task_prompt) throw new Error(`Missing 'task_prompt' in task ${task.task_id}`);
            
            if (Object.hasOwn(task, 'reference_file') && task.reference_file !== "" && task.reference_file !== null) {
                referenceFileRequired = true;
                if (Array.isArray(task.reference_file)) {
                    task.reference_file.forEach((name) => {
                        if (typeof name === 'string' && name.trim() !== '') {
                            referenceFileNames.add(name.trim());
                        }
                    });
                } else if (typeof task.reference_file === 'string' && task.reference_file.trim() !== '') {
                    referenceFileNames.add(task.reference_file.trim());
                }
            }
        });
    }

    const fileToUpload = content
        ? new File([JSON.stringify(content, null, 2)], 'task.json', { type: 'application/json' })
        : renameFile(file, 'task.json');
    return { file: fileToUpload, content, referenceFileRequired, referenceFileNames: Array.from(referenceFileNames) };
};

const validateAnswerEntry = (answer, index, seenAnswerIds, validTaskIds) => {
    if (!answer.task_id) {
        throw new Error(`Missing task_id in answer key at index ${index}`);
    }

    if (seenAnswerIds.has(answer.task_id)) {
        throw new Error(`Duplicate answer found for task_id: '${answer.task_id}'`);
    }
    seenAnswerIds.add(answer.task_id);
    
    if (answer.answer === undefined || answer.answer === null || answer.answer === "") {
        throw new Error(`Missing answer for task ${answer.task_id}`);
    }
    
    if (!validTaskIds.has(answer.task_id)) {
        throw new Error(`Incompatible ID: '${answer.task_id}' found in answers but not in tasks.`);
    }

    if (Object.hasOwn(answer, 'criteria')) {
        validateCriteria(answer.criteria, answer.task_id);
    }
};

const validateAnswerContent = (content, taskContent) => {
    if (!Array.isArray(content)) {
        throw new Error('Answer file must be a JSON Array');
    }

    const validTaskIds = new Set(taskContent.prompts.map(t => t.task_id));
    const seenAnswerIds = new Set();

    content.forEach((answer, index) => {
        validateAnswerEntry(answer, index, seenAnswerIds, validTaskIds);
    });
};

export const processAnswerFile = async (file, taskContent) => {
    if (!isValidEvalExtension(file.name)) {
        throw new Error('Invalid file. Use .json or .txt for Answers.');
    }

    if (!taskContent) {
        throw new Error('Please upload a valid Task file first.');
    }

    if (file.name.endsWith('.json')) {
        const content = await readJsonFile(file);
        validateAnswerContent(content, taskContent);
    }

    const renamedFile = renameFile(file, 'answers.json');
    return { file: renamedFile };
};

export const processReferenceFile = async (file, ownerId, onProgress, onStep, expectNames) => {
    if (!isValidArchiveExtension(file.name)) {
        throw new Error('Invalid file. Use .zip or .tar.gz.');
    }

    const ext = getArchiveExtension(file.name);
    const renamedFile = renameFile(file, `refs.${ext}`);

    try {
        if (onStep) onStep(0);
        await new Promise(r => setTimeout(r, 1000));

        if (onStep) onStep(1);
        
        const uploadResponse = await uploadFile(
            file, 
            ownerId, 
            (prog) => { if (onProgress) onProgress(prog); }
        );

        if (!uploadResponse?.filePath) throw new Error('Upload failed (no path returned)');

        if (onStep) onStep(2);
        const validation = await validateArchiveFile(uploadResponse.filePath, expectNames);
        
        if (validation.error || (validation.data && !validation.data.valid)) {
            const errorMsg = validation.data?.error || validation.message || 'Invalid archive structure';
            throw new Error(errorMsg);
        }

        return { 
            file: renamedFile, 
            uploadedPath: uploadResponse.filePath,
            extension: ext
        };

    } finally {
        if (onStep) onStep(-1);
    }
};

export const validateReferenceFileContent = async (filePath, expectNames) => {
    try {
        const response = await validateArchiveFile(filePath, expectNames);
        return response;
    } catch (error) {
        console.error('Error validating reference file:', error);
        throw error;
    }
};

export const submitEvalDataset = async (params) => {
    const {
        datasetName,
        description,
        ownerId,
        taskFile,
        answerFile,
        referenceFileObj,
        onProgress
    } = params;

    const evalFiles = [taskFile, answerFile];
    const evalId = await uploadEvalFile(
        evalFiles, 
        ownerId, 
        (p) => onProgress('eval', p)
    );

    if (!evalId) throw new Error('Failed to create Eval Bundle ID');

    const datasetPayload = {
        dataset: {
            name: datasetName,
            description: description || 'Eval Dataset',
            owner_user_id: ownerId,
            eval_id: evalId,
            file_name: null,
            file_size: 0,
            file_extension: null,
            reference_file_path: referenceFileObj?.path || null,
            reference_file_name: referenceFileObj?.name || null
        },
        comment: 'Initial Eval Dataset creation'
    };

    return await createDataset(datasetPayload);
};